import React, { memo, useCallback, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  TextField,
  InputAdornment,
  Collapse,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Search as SearchIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

/**
 * Configuration for rendering an entity in the list
 */
export interface EntityListItemConfig<TEntity> {
  /** Primary text to display */
  getPrimaryText: (entity: TEntity) => string;
  /** Secondary text to display */
  getSecondaryText: (entity: TEntity) => string;
  /** Unique identifier for the entity */
  getId: (entity: TEntity) => string;
  /** Icon component to display (optional — no icon column when absent) */
  Icon?: React.ComponentType;
  /** Optional function to get background color for non-selected items */
  getBackgroundColor?: (entity: TEntity) => string | undefined;
  /** Optional secondary typography props override */
  secondaryTypographyProps?: object;
  /** Returns text to match against search query when searchable is enabled */
  getSearchText?: (entity: TEntity) => string;
  /** A marker shown at the end of the row, such as a status chip (optional) */
  getBadge?: (entity: TEntity) => React.ReactNode;
  /** A test id for the row (optional) */
  getTestId?: (entity: TEntity) => string;
}

/**
 * Props for EntityListPanel component
 */
export interface EntityListPanelProps<TEntity> {
  /** List of entities to display */
  entities: TEntity[];
  /** Currently selected entity */
  selectedEntity: TEntity | null;
  /** Whether we're in "add new" mode */
  isNew: boolean;
  /** Whether save operation is in progress */
  saving: boolean;
  /** Configuration for rendering list items */
  config: EntityListItemConfig<TEntity>;
  /** Button text for adding new entity (only needed when onAddNew is provided) */
  addButtonText?: string;
  /** Text to show when no entities exist */
  emptyStateText: string;
  /** Helper text for empty state */
  emptyStateHelperText: string;
  /** Callback when an entity is selected */
  onSelect: (entity: TEntity) => void;
  /** Callback when "Add New" is clicked (optional — no button when absent) */
  onAddNew?: () => void;
  /** Content rendered in the header area above the list */
  headerContent?: React.ReactNode;
  /** Whether the list is loading */
  isLoading?: boolean;
  /** When true, renders a built-in search field; requires config.getSearchText */
  searchable?: boolean;
  /** When provided, groups entities into collapsible sections. Return multiple groups to place an entity under each. Empty array → ungrouped. */
  groupBy?: (entity: TEntity) => Array<{ key: string; label: string }>;
}

/**
 * Memoized list item component for better performance
 */
interface EntityListItemProps<TEntity> {
  entity: TEntity;
  isSelected: boolean;
  config: EntityListItemConfig<TEntity>;
  onSelect: (entity: TEntity) => void;
}

const EntityListItem = memo(<TEntity,>({
  entity,
  isSelected,
  config,
  onSelect
}: EntityListItemProps<TEntity>) => {
  const { getPrimaryText, getSecondaryText, getId, Icon, getBackgroundColor, secondaryTypographyProps, getBadge, getTestId } = config;
  const id = getId(entity);
  const backgroundColor = getBackgroundColor ? getBackgroundColor(entity) : undefined;

  const handleClick = useCallback(() => {
    onSelect(entity);
  }, [entity, onSelect]);

  return (
    <ListItem key={id} disablePadding sx={{ mb: 0.5 }}>
      <ListItemButton
        selected={isSelected}
        onClick={handleClick}
        data-testid={getTestId?.(entity)}
        sx={{
          borderRadius: 1,
          ...(!isSelected && backgroundColor ? { bgcolor: backgroundColor } : {}),
          '&&.Mui-selected': {
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
            ...(Icon ? {
              '& .MuiListItemIcon-root': {
                color: 'primary.contrastText',
              },
            } : {}),
            '& .MuiListItemText-secondary': {
              color: (theme) => alpha(theme.palette.primary.contrastText, 0.7),
            },
          }
        }}
      >
        {Icon && (
          <ListItemIcon>
            <Icon />
          </ListItemIcon>
        )}
        <ListItemText
          primary={getPrimaryText(entity)}
          secondary={getSecondaryText(entity)}
          {...(secondaryTypographyProps ? { secondaryTypographyProps } : {})}
        />
        {getBadge?.(entity)}
      </ListItemButton>
    </ListItem>
  );
}) as <TEntity>(props: EntityListItemProps<TEntity>) => React.JSX.Element;

interface GroupSectionProps<TEntity> {
  label: string;
  groupKey: string;
  entities: TEntity[];
  collapsed: boolean;
  onToggle: (key: string) => void;
  selectedEntity: TEntity | null;
  config: EntityListItemConfig<TEntity>;
  onSelect: (entity: TEntity) => void;
}

const GroupSection = memo(<TEntity,>({
  label,
  groupKey,
  entities,
  collapsed,
  onToggle,
  selectedEntity,
  config,
  onSelect,
}: GroupSectionProps<TEntity>) => {
  const { getId } = config;

  const handleToggle = useCallback(() => {
    onToggle(groupKey);
  }, [groupKey, onToggle]);

  return (
    <>
      <ListItem
        disablePadding
        sx={{ mb: 0.25 }}
        onClick={handleToggle}
      >
        <ListItemButton sx={{ borderRadius: 1, py: 0.5 }}>
          <ListItemText
            primary={
              <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {label}
              </Typography>
            }
          />
          {collapsed ? <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} /> : <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
        </ListItemButton>
      </ListItem>
      <Collapse in={!collapsed} timeout="auto">
        {entities.map((entity) => {
          const id = getId(entity);
          const isSelected = Boolean(selectedEntity && getId(selectedEntity) === id);
          return (
            <EntityListItem
              key={`${groupKey}:${id}`}
              entity={entity}
              isSelected={isSelected}
              config={config}
              onSelect={onSelect}
            />
          );
        })}
      </Collapse>
    </>
  );
}) as <TEntity>(props: GroupSectionProps<TEntity>) => React.JSX.Element;

/**
 * Generic list panel for entity management modals
 * Displays a sidebar with list of entities and optional "Add New" button
 */
export function EntityListPanel<TEntity>({
  entities,
  selectedEntity,
  isNew,
  saving,
  config,
  addButtonText,
  emptyStateText,
  emptyStateHelperText,
  onSelect,
  onAddNew,
  headerContent,
  isLoading,
  searchable,
  groupBy,
}: EntityListPanelProps<TEntity>) {
  if (searchable && !config.getSearchText) {
    throw new Error('EntityListPanel: searchable requires getSearchText on config');
  }

  const { getId } = config;
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const showHeader = headerContent || onAddNew || searchable;

  const filteredEntities = searchable && searchQuery && config.getSearchText
    ? entities.filter((entity) =>
        config.getSearchText!(entity).toLowerCase().includes(searchQuery.toLowerCase())
      )
    : entities;

  const toggleGroup = useCallback((key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const renderList = () => {
    if (groupBy) {
      const groupMap = new Map<string, { label: string; items: TEntity[] }>();
      for (const entity of filteredEntities) {
        const groups = groupBy(entity);
        const targets = groups.length === 0
          ? [{ key: '__ungrouped__', label: 'Ungrouped' }]
          : groups;
        for (const { key, label } of targets) {
          if (!groupMap.has(key)) {
            groupMap.set(key, { label, items: [] });
          }
          groupMap.get(key)!.items.push(entity);
        }
      }

      return (
        <List sx={{ flex: 1, overflow: 'auto', p: 1 }}>
          {Array.from(groupMap.entries()).map(([key, { label, items }]) => (
            <GroupSection
              key={key}
              label={label}
              groupKey={key}
              entities={items}
              collapsed={!expandedGroups.has(key)}
              onToggle={toggleGroup}
              selectedEntity={selectedEntity}
              config={config}
              onSelect={onSelect}
            />
          ))}
          {isNew && renderNewItem()}
        </List>
      );
    }

    return (
      <List sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {filteredEntities.map((entity) => {
          const id = getId(entity);
          const isSelected = Boolean(selectedEntity && getId(selectedEntity) === id && !isNew);

          return (
            <EntityListItem
              key={id}
              entity={entity}
              isSelected={isSelected}
              config={config}
              onSelect={onSelect}
            />
          );
        })}
        {isNew && renderNewItem()}
      </List>
    );
  };

  const renderNewItem = () => (
    <ListItem disablePadding sx={{ mb: 0.5 }}>
      <ListItemButton
        selected
        sx={{
          borderRadius: 1,
          bgcolor: 'success.main',
          color: 'success.contrastText',
          '&:hover': {
            bgcolor: 'success.dark',
          },
          '& .MuiListItemIcon-root': {
            color: 'success.contrastText',
          }
        }}
      >
        <ListItemIcon>
          <AddIcon />
        </ListItemIcon>
        <ListItemText
          primary="New Item"
          secondary="Unsaved"
          secondaryTypographyProps={{
            sx: { color: (theme) => alpha(theme.palette.success.contrastText, 0.7) }
          }}
        />
      </ListItemButton>
    </ListItem>
  );

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {showHeader && (
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          {headerContent}
          {searchable && (
            <TextField
              fullWidth
              size="small"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={headerContent ? { mt: 1 } : undefined}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}
          {onAddNew && (
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onAddNew}
              disabled={saving}
              sx={headerContent || searchable ? { mt: 1 } : undefined}
            >
              {addButtonText}
            </Button>
          )}
        </Box>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        renderList()
      )}

      {filteredEntities.length === 0 && !isNew && !isLoading && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {searchQuery ? 'No results found' : emptyStateText}
          </Typography>
          {!searchQuery && (
            <Typography variant="caption" color="text.secondary">
              {emptyStateHelperText}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
